import type { AlgorithmChallenge } from '@code-arcade/engine'

export const quickselect: AlgorithmChallenge = {
  id: 'quickselect',
  title: 'Quickselect',
  level: 3,
  chapter: 'chapter-4',
  mode: 'race-it',
  description: 'Partition the array around a pivot to find the K-th element in O(n) — no full sort required.',

  realWorld: {
    scenario: "Omnitech's marketing team needs the top 1,000 highest-engagement users out of 50M for a targeted campaign. The pipeline currently does Array.sort() then slice(0, 1000) — sorting 50 million records to return 1,000.",
    consequence: 'O(n log n) on 50M ≈ 1.3 billion operations, 4GB of sorted data thrown away. Quickselect finds the threshold in O(n) — 50M comparisons, zero full sort.',
    products: [],
  },

  visualization: {
    type: 'bar-array',
    defaultSize: 16,
    allowCustomInput: false,
  },

  interrupts: [
    {
      atStep: 20,
      character: 'vera-lox',
      mood: 'urgent',
      message: "We need those user segments in 90 seconds. You're sorting 50M records for 1,000 results.",
    },
  ],

  complexity: { time: 'O(n) average · O(n²) worst', space: 'O(log n)' },
  xpReward: { oneStar: 200, twoStar: 400, threeStar: 800 },

  keyTakeaway:
    'You rarely need everything sorted — you need the top K, the median, or the threshold. ' +
    'Quickselect partitions around a pivot and recurses only on the side containing your target, ' +
    'turning O(n log n) into O(n) average. Database TOP N queries use this. Streaming median ' +
    'algorithms use this. Percentile calculations in every monitoring system use this. Every time ' +
    'a ticket says "find the K most/least/fastest," ask whether you\'re sorting when you should be partitioning.',

  whenToUse: [
    'Top-K leaderboards — find the threshold without sorting the full list',
    'p50/p95/p99 percentile calculations in monitoring and APM systems',
    '"Find the median salary" in one pass — no full sort',
    'Database query planners choosing between nested loop vs hash join based on a cardinality threshold',
    'Any "find K-th largest/smallest" problem — the pattern is always the same',
  ],

  whenNotToUse: [
    'You need the top K results ranked among themselves — quickselect finds the boundary, not the order',
    'Worst-case guarantees matter — random pivot degrades to O(n²) on adversarial input (use introselect)',
    'K is close to n — at that point you might as well sort',
  ],

  pseudocode: `procedure quickselect(A, low, high, k)
  if low = high then return A[low]
  pivotPos ← partition(A, low, high)
  if pivotPos = k then return A[pivotPos]
  else if pivotPos < k then
    return quickselect(A, pivotPos + 1, high, k)
  else
    return quickselect(A, low, pivotPos - 1, k)

procedure partition(A, low, high)
  pivot ← A[high]
  i ← low − 1
  for j from low to high − 1
    if A[j] ≤ pivot
      i ← i + 1
      swap A[i] and A[j]
  swap A[i + 1] and A[high]
  return i + 1`,

  codeSnippets: [
    {
      language: 'csharp',
      label: 'C#',
      code: `public static int Quickselect(int[] arr, int low, int high, int k) {
    if (low == high) return arr[low];
    int pivotPos = Partition(arr, low, high);
    if (pivotPos == k) return arr[pivotPos];
    return pivotPos < k
        ? Quickselect(arr, pivotPos + 1, high, k)
        : Quickselect(arr, low, pivotPos - 1, k);
}

static int Partition(int[] arr, int low, int high) {
    int pivot = arr[high], i = low - 1;
    for (int j = low; j < high; j++)
        if (arr[j] <= pivot) { i++; (arr[i], arr[j]) = (arr[j], arr[i]); }
    (arr[i + 1], arr[high]) = (arr[high], arr[i + 1]);
    return i + 1;
}

// Array.sort() IS this algorithm (TimSort = merge sort + insertion sort). You just saw its internals:
// For top-K without sorting, use the pattern above — no built-in equivalent`,
    },
    {
      language: 'python',
      label: 'Python',
      code: `import random

def quickselect(arr: list[int], k: int) -> int:
    if len(arr) == 1:
        return arr[0]
    pivot = random.choice(arr)
    left  = [x for x in arr if x < pivot]
    mid   = [x for x in arr if x == pivot]
    right = [x for x in arr if x > pivot]
    if k < len(left):
        return quickselect(left, k)
    elif k < len(left) + len(mid):
        return pivot
    else:
        return quickselect(right, k - len(left) - len(mid))

# Python stdlib equivalent for nth element:
import statistics
median = statistics.median(arr)  # O(n) internally uses introselect`,
    },
    {
      language: 'javascript',
      label: 'JavaScript',
      code: `function quickselect(arr, k, low = 0, high = arr.length - 1) {
  if (low === high) return arr[low];
  const pivotPos = partition(arr, low, high);
  if (pivotPos === k) return arr[pivotPos];
  return pivotPos < k
    ? quickselect(arr, k, pivotPos + 1, high)
    : quickselect(arr, k, low, pivotPos - 1);
}

function partition(arr, low, high) {
  const pivot = arr[high];
  let i = low - 1;
  for (let j = low; j < high; j++)
    if (arr[j] <= pivot) [arr[++i], arr[j]] = [arr[j], arr[i]];
  [arr[i + 1], arr[high]] = [arr[high], arr[i + 1]];
  return i + 1;
}

// No built-in for nth element — but you now know exactly how to build one`,
    },
    {
      language: 'typescript',
      label: 'TypeScript',
      code: `function quickselect(arr: number[], k: number, low = 0, high = arr.length - 1): number {
  if (low === high) return arr[low];
  const pivotPos = partition(arr, low, high);
  if (pivotPos === k) return arr[pivotPos];
  return pivotPos < k
    ? quickselect(arr, k, pivotPos + 1, high)
    : quickselect(arr, k, low, pivotPos - 1);
}

function partition(arr: number[], low: number, high: number): number {
  const pivot = arr[high];
  let i = low - 1;
  for (let j = low; j < high; j++)
    if (arr[j] <= pivot) [arr[++i], arr[j]] = [arr[j], arr[i]];
  [arr[i + 1], arr[high]] = [arr[high], arr[i + 1]];
  return i + 1;
}

// No built-in for nth element — but you now know exactly how to build one`,
    },
    {
      language: 'java',
      label: 'Java',
      code: `public static int quickselect(int[] arr, int low, int high, int k) {
    if (low == high) return arr[low];
    int pivotPos = partition(arr, low, high);
    if (pivotPos == k) return arr[pivotPos];
    return pivotPos < k
        ? quickselect(arr, pivotPos + 1, high, k)
        : quickselect(arr, low, pivotPos - 1, k);
}

static int partition(int[] arr, int low, int high) {
    int pivot = arr[high], i = low - 1;
    for (int j = low; j < high; j++) {
        if (arr[j] <= pivot) { int t = arr[++i]; arr[i] = arr[j]; arr[j] = t; }
    }
    int t = arr[i + 1]; arr[i + 1] = arr[high]; arr[high] = t;
    return i + 1;
}

// No built-in nth-element in Java — Arrays.sort() does not expose a partial partition`,
    },
  ],

  sampleDataset: {
    label: 'Omnitech user engagement scores — 50M users, find top-25% campaign threshold',
    preview: [8241, 3892, 9104, 1533, 7720, 4481, 6390, 2105, 8875, 5542, 9931, 3017],
    atScale: {
      n: 50000000,
      leftLabel: 'Array.sort().slice(0,1000)',
      leftNote: '~1.3B ops, 4GB sorted',
      rightLabel: 'Quickselect',
      rightNote: '~50M ops, O(log n) space',
    },
  },
}
